import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  TbUsersGroup,
  TbPlus,
  TbTrash,
  TbPencil,
  TbCheck,
  TbX,
  TbUserPlus,
} from "react-icons/tb";
import { useAuth } from "../../../auth/AuthContext";
import { useTenantRole } from "../../../hooks/useTenantRole";
import { listMembers, createProfessional } from "../../../services/memberService";
import { handleFeatureNotIncluded } from "../../../utils/notifyPlanUpgrade";
import {
  listDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  addDepartmentMember,
  removeDepartmentMember,
} from "../../../services/departmentService";
import { stringToColor } from "../../../components/CalendarCalendar/calendarHelpers";
import { ButtonBack, EmptyState } from "../../../components/ui";
import { exampleDepartmentName } from "./segmentExample";
import AddProfessionalModal from "./AddProfessionalModal";
import styles from "./DepartmentsSettings.module.css";

const DEFAULT_COLOR = "#3498db";

const ROLE_LABEL = {
  OWNER: "Proprietário",
  ADMIN: "Admin",
  MEMBER: "Membro",
};

/** Cor exibida no marcador do setor: usa a cor salva ou deriva do nome. */
function dotColor(department) {
  return department?.color || stringToColor(department?.name);
}

/** Nome exibível de um TenantMember (nome → e-mail → fallback). */
function memberName(tm) {
  return tm?.user?.name || tm?.user?.email || "Profissional";
}

function initialOf(name) {
  return (name || "?").charAt(0).toUpperCase();
}

/** Descrição secundária de um profissional: especialidade ou o papel. */
function memberMeta(tm) {
  return tm?.specialty || ROLE_LABEL[tm?.role] || "Membro";
}

/**
 * Hub de gestão da equipe **por setor** (setor-first) — F2.3 evoluída.
 *
 * Cada setor é a unidade principal: mostra os profissionais que contém e é de
 * DENTRO dele que se adiciona gente (vincular um TenantMember existente ou criar
 * um novo já vinculado). Uma seção "Sem setor" lista quem não está em nenhum
 * setor — estado válido: continuam ativos e enxergam tudo (escopo F2.4). N:N é
 * respeitado: um profissional pode aparecer em vários setores; "remover do
 * setor" desfaz só o vínculo (`DepartmentMember`), nunca apaga o profissional.
 *
 * Visível só a OWNER/ADMIN (rota protegida por `RoleRoute`); as ações de mutação
 * são, além disso, gateadas por `useTenantRole` para defesa em profundidade.
 */
export default function DepartmentsSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const segment = user?.tenant?.segment;
  const { role } = useTenantRole();
  const canManage = role === "OWNER" || role === "ADMIN";

  const [departments, setDepartments] = useState([]);
  const [members, setMembers] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  // Criação de setor
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(DEFAULT_COLOR);
  const [creating, setCreating] = useState(false);

  // Edição inline de setor
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState(DEFAULT_COLOR);
  const [savingEdit, setSavingEdit] = useState(false);

  // Modal "adicionar profissional" — id do setor alvo (ou null)
  const [addModalDeptId, setAddModalDeptId] = useState(null);
  const [modalBusy, setModalBusy] = useState(false);

  // Picker inline "adicionar a setor" na seção Sem setor — id do membro alvo
  const [linkingMemberId, setLinkingMemberId] = useState(null);

  const fetchData = useCallback(async () => {
    setStatus("loading");
    try {
      const [depsRes, membersRes] = await Promise.all([
        listDepartments(),
        listMembers(),
      ]);
      setDepartments(Array.isArray(depsRes.data) ? depsRes.data : []);
      setMembers(Array.isArray(membersRes.data) ? membersRes.data : []);
      setStatus("ready");
    } catch {
      setStatus("error");
      toast.error("Erro ao carregar os setores.");
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Recarrega só a lista de setores (após vínculo/remoção) sem re-piscar a página.
  const reloadDepartments = useCallback(async () => {
    try {
      const res = await listDepartments();
      setDepartments(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Erro ao atualizar os setores.");
    }
  }, []);

  // Recarrega setores + membros (após criar profissional novo) sem loading.
  const reloadAll = useCallback(async () => {
    try {
      const [depsRes, membersRes] = await Promise.all([
        listDepartments(),
        listMembers(),
      ]);
      setDepartments(Array.isArray(depsRes.data) ? depsRes.data : []);
      setMembers(Array.isArray(membersRes.data) ? membersRes.data : []);
    } catch {
      toast.error("Erro ao atualizar a equipe.");
    }
  }, []);

  // ── Setores presentes por membro (para computar "sem setor" e disponíveis) ──
  const assignedMemberIds = useMemo(() => {
    const set = new Set();
    for (const dept of departments) {
      for (const dm of dept.members || []) set.add(dm.memberId);
    }
    return set;
  }, [departments]);

  const unassignedMembers = useMemo(
    () => members.filter((m) => !assignedMemberIds.has(m.id)),
    [members, assignedMemberIds],
  );

  // Setor cujo modal está aberto + membros disponíveis (não vinculados a ele).
  const addModalDept = useMemo(
    () => departments.find((d) => d.id === addModalDeptId) || null,
    [departments, addModalDeptId],
  );

  const availableForModal = useMemo(() => {
    if (!addModalDept) return [];
    const inDept = new Set((addModalDept.members || []).map((dm) => dm.memberId));
    return members.filter((m) => !inDept.has(m.id));
  }, [addModalDept, members]);

  // ── Criar setor ────────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) {
      toast.error("Informe o nome do setor.");
      return;
    }
    setCreating(true);
    try {
      await createDepartment({ name, color: newColor });
      toast.success("Setor criado!");
      setNewName("");
      setNewColor(DEFAULT_COLOR);
      await reloadDepartments();
    } catch (err) {
      if (err.response?.data?.code === "DEPARTMENT_NAME_TAKEN") {
        toast.error("Já existe um setor com esse nome. Escolha outro.");
      } else {
        toast.error(err.response?.data?.error || "Erro ao criar o setor.");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleUseExample = () => setNewName(exampleDepartmentName(segment));

  // ── Editar setor ─────────────────────────────────────────────────────────
  const startEdit = (dept) => {
    setEditingId(dept.id);
    setEditName(dept.name);
    setEditColor(dept.color || DEFAULT_COLOR);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
  };

  const handleSaveEdit = async (dept) => {
    const name = editName.trim();
    if (!name) {
      toast.error("O nome do setor não pode ficar vazio.");
      return;
    }
    setSavingEdit(true);
    try {
      await updateDepartment(dept.id, { name, color: editColor });
      toast.success("Setor atualizado!");
      cancelEdit();
      await reloadDepartments();
    } catch (err) {
      if (err.response?.data?.code === "DEPARTMENT_NAME_TAKEN") {
        toast.error("Já existe um setor com esse nome. Escolha outro.");
      } else {
        toast.error(err.response?.data?.error || "Erro ao salvar o setor.");
      }
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Ativar / desativar setor ───────────────────────────────────────────────
  const handleToggleActive = async (dept) => {
    try {
      await updateDepartment(dept.id, { isActive: !dept.isActive });
      setDepartments((prev) =>
        prev.map((d) =>
          d.id === dept.id ? { ...d, isActive: !dept.isActive } : d,
        ),
      );
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao alterar o status.");
    }
  };

  // ── Excluir setor ──────────────────────────────────────────────────────────
  const handleDelete = async (dept) => {
    if (!window.confirm(`Excluir o setor "${dept.name}"?`)) return;
    try {
      await deleteDepartment(dept.id);
      toast.success("Setor excluído.");
      setDepartments((prev) => prev.filter((d) => d.id !== dept.id));
    } catch (err) {
      if (err.response?.data?.code === "DEPARTMENT_IN_USE") {
        toast.error(
          "Este setor está em uso por conversas. Desative-o em vez de excluir.",
        );
      } else {
        toast.error(err.response?.data?.error || "Erro ao excluir o setor.");
      }
    }
  };

  // ── Vincular membro existente (via modal) ───────────────────────────────────
  const handleLinkExisting = async (memberId) => {
    const deptId = addModalDeptId;
    setModalBusy(true);
    try {
      await addDepartmentMember(deptId, memberId);
      toast.success("Profissional adicionado ao setor.");
      setAddModalDeptId(null);
      await reloadDepartments();
    } catch (err) {
      if (err.response?.data?.code === "MEMBER_ALREADY_IN_DEPARTMENT") {
        toast.error("Este profissional já está no setor.");
        setAddModalDeptId(null);
        await reloadDepartments();
      } else {
        toast.error(err.response?.data?.error || "Erro ao adicionar ao setor.");
      }
    } finally {
      setModalBusy(false);
    }
  };

  // ── Criar profissional novo e vincular ao setor (2 chamadas sequenciais) ─────
  const handleCreateAndLink = async (payload) => {
    const deptId = addModalDeptId;
    setModalBusy(true);

    // 1) cria o profissional
    let newMemberId;
    try {
      const res = await createProfessional(payload);
      newMemberId = res.data?.member?.id;
      const tempPassword = res.data?.tempPassword;
      if (tempPassword) {
        try {
          await navigator.clipboard.writeText(tempPassword);
          toast.success(
            "Profissional criado! Senha temporária copiada para a área de transferência.",
            { autoClose: 8000 },
          );
        } catch {
          toast.success(`Profissional criado! Senha temporária: ${tempPassword}`, {
            autoClose: 8000,
          });
        }
      }
    } catch (err) {
      // 403 FEATURE_NOT_INCLUDED ≠ 403 LIMIT_REACHED (logo abaixo). ⚠️ Esta
      // tela NÃO está atrás do SchedulingRoute, então o 403 é alcançável
      // aqui mesmo fora da janela fail-safe. ADR-0026.
      if (handleFeatureNotIncluded(err)) {
        setModalBusy(false);
        return;
      }
      const errStatus = err.response?.status;
      const data = err.response?.data;
      if (errStatus === 409) {
        toast.error(data?.error || "E-mail já cadastrado.");
      } else if (errStatus === 403 && data?.code === "LIMIT_REACHED") {
        toast.error("Limite de membros do seu plano atingido.");
      } else {
        toast.error("Erro ao criar profissional. Tente novamente.");
      }
      setModalBusy(false);
      return; // não segue para o vínculo se a criação falhou
    }

    // 2) vincula ao setor — erro parcial não perde o profissional criado
    if (newMemberId) {
      try {
        await addDepartmentMember(deptId, newMemberId);
        toast.success("Profissional adicionado ao setor.");
      } catch {
        toast.warning(
          "Profissional criado, mas não foi possível vinculá-lo ao setor. Vincule-o na seção \"Sem setor\".",
        );
      }
    }

    setAddModalDeptId(null);
    setModalBusy(false);
    await reloadAll();
  };

  // ── Remover vínculo de um profissional com um setor (N:N) ───────────────────
  const handleRemoveFromDept = async (dept, dm) => {
    const name = memberName(dm.member);
    const ok = window.confirm(
      `Remover ${name} do setor "${dept.name}"?\n\n` +
        "Isso remove apenas o vínculo com o setor — o profissional continua cadastrado.",
    );
    if (!ok) return;
    try {
      await removeDepartmentMember(dept.id, dm.memberId);
      toast.success("Profissional removido do setor.");
      await reloadDepartments();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao remover do setor.");
    }
  };

  // ── Vincular a partir da seção "Sem setor" ──────────────────────────────────
  const handleLinkUnassigned = async (deptId, memberId) => {
    try {
      await addDepartmentMember(deptId, memberId);
      toast.success("Profissional adicionado ao setor.");
      setLinkingMemberId(null);
      await reloadDepartments();
    } catch (err) {
      if (err.response?.data?.code === "MEMBER_ALREADY_IN_DEPARTMENT") {
        toast.error("Este profissional já está no setor.");
        setLinkingMemberId(null);
        await reloadDepartments();
      } else {
        toast.error(err.response?.data?.error || "Erro ao adicionar ao setor.");
      }
    }
  };

  // ── Render: estados de carga/erro ───────────────────────────────────────────
  if (status === "loading") {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <p className={styles.muted}>Carregando setores...</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className={styles.page}>
        <EmptyState
          role="alert"
          icon="⚠️"
          title="Não foi possível carregar os setores"
          description="Ocorreu um erro ao buscar os setores. Tente novamente."
          action={
            <button type="button" className={styles.retryBtn} onClick={fetchData}>
              Tentar novamente
            </button>
          }
        />
      </div>
    );
  }

  const isEmpty = departments.length === 0;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <ButtonBack to="/business/settings" />
        <div>
          <h1 className={styles.title}>Setores</h1>
          <p className={styles.subtitle}>
            Organize sua equipe em setores (ex.: atendimento, financeiro) e
            gerencie os profissionais de dentro de cada um.
          </p>
        </div>
      </header>

      {/* ── Criar setor (só gestão) ──────────────────────────────────────── */}
      {canManage && (
        <section className={styles.card} aria-labelledby="create-title">
          <h2 id="create-title" className={styles.cardTitle}>
            Novo setor
          </h2>
          <form className={styles.createForm} onSubmit={handleCreate}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="dept-name">
                Nome do setor
              </label>
              <input
                id="dept-name"
                type="text"
                className={styles.input}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ex.: Atendimento"
                maxLength={60}
              />
            </div>
            <div className={styles.colorField}>
              <label className={styles.label} htmlFor="dept-color">
                Cor
              </label>
              <input
                id="dept-color"
                type="color"
                className={styles.colorInput}
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                aria-label="Cor do setor"
              />
            </div>
            <button type="submit" className={styles.primaryBtn} disabled={creating}>
              <TbPlus size={16} aria-hidden="true" />
              {creating ? "Criando..." : "Criar setor"}
            </button>
          </form>
        </section>
      )}

      {/* ── Estado vazio (nenhum setor) ──────────────────────────────────── */}
      {isEmpty ? (
        <EmptyState
          icon={<TbUsersGroup size={40} aria-hidden="true" />}
          title="Você ainda não tem setores"
          description="Crie seu primeiro setor para começar a organizar a equipe."
          action={
            canManage ? (
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={handleUseExample}
              >
                <TbPlus size={16} aria-hidden="true" />
                Criar seu primeiro setor ({exampleDepartmentName(segment)})
              </button>
            ) : null
          }
        />
      ) : (
        /* ── Lista de setores (setor-first) ─────────────────────────────── */
        <ul className={styles.list}>
          {departments.map((dept) => {
            const editing = editingId === dept.id;
            const deptMembers = dept.members || [];
            const count = deptMembers.length;
            return (
              <li key={dept.id} className={styles.deptCard}>
                <div className={styles.deptRow}>
                  <span
                    className={styles.dot}
                    style={{ background: editing ? editColor : dotColor(dept) }}
                    aria-hidden="true"
                  />

                  {editing ? (
                    <div className={styles.editArea}>
                      <input
                        type="text"
                        className={styles.input}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        maxLength={60}
                        aria-label="Nome do setor"
                      />
                      <input
                        type="color"
                        className={styles.colorInput}
                        value={editColor}
                        onChange={(e) => setEditColor(e.target.value)}
                        aria-label="Cor do setor"
                      />
                      <button
                        type="button"
                        className={styles.iconBtn}
                        onClick={() => handleSaveEdit(dept)}
                        disabled={savingEdit}
                        aria-label="Salvar setor"
                      >
                        <TbCheck size={18} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className={styles.iconBtn}
                        onClick={cancelEdit}
                        aria-label="Cancelar edição"
                      >
                        <TbX size={18} aria-hidden="true" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className={styles.deptName}>
                        {dept.name}
                        {!dept.isActive && (
                          <span className={styles.inactivePill}>inativo</span>
                        )}
                      </span>

                      <span className={styles.countPill}>
                        {count} {count === 1 ? "profissional" : "profissionais"}
                      </span>

                      {canManage && (
                        <div className={styles.deptActions}>
                          <label className={styles.switch}>
                            <input
                              type="checkbox"
                              checked={!!dept.isActive}
                              onChange={() => handleToggleActive(dept)}
                              aria-label={
                                dept.isActive
                                  ? `Desativar setor ${dept.name}`
                                  : `Ativar setor ${dept.name}`
                              }
                            />
                            <span className={styles.slider} aria-hidden="true" />
                          </label>

                          <button
                            type="button"
                            className={styles.iconBtn}
                            onClick={() => startEdit(dept)}
                            aria-label={`Editar setor ${dept.name}`}
                          >
                            <TbPencil size={16} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className={`${styles.iconBtn} ${styles.danger}`}
                            onClick={() => handleDelete(dept)}
                            aria-label={`Excluir setor ${dept.name}`}
                          >
                            <TbTrash size={16} aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Corpo: profissionais do setor */}
                {!editing && (
                  <div className={styles.deptBody}>
                    {count === 0 ? (
                      <p className={styles.emptyDept}>
                        Nenhum profissional neste setor ainda.
                      </p>
                    ) : (
                      <ul className={styles.profList}>
                        {deptMembers.map((dm) => {
                          const name = memberName(dm.member);
                          return (
                            <li key={dm.id} className={styles.profRow}>
                              <span
                                className={styles.profAvatar}
                                style={{ background: stringToColor(name) }}
                                aria-hidden="true"
                              >
                                {initialOf(name)}
                              </span>
                              <span className={styles.profInfo}>
                                <span className={styles.profName}>{name}</span>
                                <span className={styles.profMeta}>
                                  {memberMeta(dm.member)}
                                </span>
                              </span>
                              {canManage && (
                                <button
                                  type="button"
                                  className={styles.removeBtn}
                                  onClick={() => handleRemoveFromDept(dept, dm)}
                                  aria-label={`Remover ${name} do setor ${dept.name}`}
                                  title="Remover do setor"
                                >
                                  <TbX size={16} aria-hidden="true" />
                                </button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    {canManage && (
                      <button
                        type="button"
                        className={styles.addProfBtn}
                        onClick={() => setAddModalDeptId(dept.id)}
                      >
                        <TbUserPlus size={16} aria-hidden="true" />
                        Adicionar profissional
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* ── Seção "Sem setor" ────────────────────────────────────────────── */}
      {(!isEmpty || unassignedMembers.length > 0) && (
        <section className={styles.card} aria-labelledby="no-dept-title">
          <h2 id="no-dept-title" className={styles.cardTitle}>
            Sem setor
          </h2>
          <p className={styles.muted}>
            Profissionais que não estão em nenhum setor. Eles continuam ativos e
            atendem normalmente — o setor é opcional.
          </p>

          {unassignedMembers.length === 0 ? (
            <EmptyState
              size="sm"
              icon={<TbUsersGroup size={28} aria-hidden="true" />}
              title={
                members.length === 0
                  ? "Nenhum profissional cadastrado ainda"
                  : "Todos os profissionais estão em algum setor"
              }
              description={
                members.length === 0
                  ? "Cadastre profissionais para montar sua equipe."
                  : "Bom trabalho — toda a equipe está organizada."
              }
              action={
                canManage && members.length === 0 ? (
                  <button
                    type="button"
                    className={styles.primaryBtn}
                    onClick={() => navigate("/business/professionals/new")}
                  >
                    <TbUserPlus size={16} aria-hidden="true" />
                    Cadastrar profissional
                  </button>
                ) : null
              }
            />
          ) : (
            <ul className={styles.profList}>
              {unassignedMembers.map((m) => {
                const name = memberName(m);
                const picking = linkingMemberId === m.id;
                return (
                  <li key={m.id} className={styles.profRow}>
                    <span
                      className={styles.profAvatar}
                      style={{ background: stringToColor(name) }}
                      aria-hidden="true"
                    >
                      {initialOf(name)}
                    </span>
                    <span className={styles.profInfo}>
                      <span className={styles.profName}>{name}</span>
                      <span className={styles.profMeta}>{memberMeta(m)}</span>
                    </span>

                    {canManage &&
                      !isEmpty &&
                      (picking ? (
                        <div className={styles.chipRow}>
                          {departments.map((d) => (
                            <button
                              key={d.id}
                              type="button"
                              className={styles.chip}
                              onClick={() => handleLinkUnassigned(d.id, m.id)}
                            >
                              <span
                                className={styles.chipDot}
                                style={{ background: dotColor(d) }}
                                aria-hidden="true"
                              />
                              {d.name}
                            </button>
                          ))}
                          <button
                            type="button"
                            className={styles.iconBtn}
                            onClick={() => setLinkingMemberId(null)}
                            aria-label="Cancelar"
                          >
                            <TbX size={16} aria-hidden="true" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={styles.linkBtn}
                          onClick={() => setLinkingMemberId(m.id)}
                        >
                          <TbPlus size={14} aria-hidden="true" />
                          Adicionar a setor
                        </button>
                      ))}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* ── Modal "adicionar profissional" ───────────────────────────────── */}
      {addModalDept && (
        <AddProfessionalModal
          department={addModalDept}
          availableMembers={availableForModal}
          onLinkExisting={handleLinkExisting}
          onCreateAndLink={handleCreateAndLink}
          onClose={() => setAddModalDeptId(null)}
          busy={modalBusy}
        />
      )}
    </div>
  );
}
